import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation } from 'react-router-dom';

const SEO = ({
  title,
  description,
  canonical,
  noindex = false,
  ogTitle,
  ogDescription,
  ogImage,
  structuredData,
  keywords,
  lang = 'en',
  alternateLanguages
}) => {
  const location = useLocation();
  const baseUrl = process.env.REACT_APP_BASE_URL || 'https://qualitati.com';

  // Generate canonical URL
  const canonicalUrl = canonical || `${baseUrl}${location.pathname}`;

  // Determine product name based on current path
  const getProductName = () => {
    if (location.pathname.startsWith('/surveys') || location.pathname.startsWith('/s/')) {
      return 'Qualitati Surveys';
    } else if (location.pathname === '/themelens' || location.pathname === '/theme-analysis' || location.pathname === '/pdf-analysis') {
      return 'Qualitati ThemeLens';
    } else if (location.pathname === '/projects' || location.pathname.match(/^\/projects\/[^/]+$/)) {
      return 'Qualitati';
    } else if (location.pathname.includes('/interview')) {
      return 'Qualitati';
    }
    return 'Qualitati';
  };

  const productName = getProductName();

  // Default meta values
  const defaultTitle = `${productName} — Free AI Qualitative Research Software | NVivo Alternative`;
  const defaultDescription = 'Free AI qualitative research software and NVivo alternative. AI interview tool for PhD researchers, thematic analysis tool, qualitative data analysis with inductive coding, and conversational surveys. Qualtrics alternative built for academics.';
  const defaultKeywords = 'AI qualitative research, NVivo alternative, qualitative research software, AI interview tool, thematic analysis tool, qualitative data analysis, PhD interview tool, Qualtrics alternative, free qualitative analysis software, inductive coding, deductive coding, QDA software, academic research platform, Qualitati';

  const pageTitle = title ? `${title} | ${productName}` : defaultTitle;
  const pageDescription = description || defaultDescription;
  const pageOgTitle = ogTitle || pageTitle;
  const pageOgDescription = ogDescription || pageDescription;
  const pageOgImage = ogImage || `${baseUrl}/logo512.png`;
  const pageKeywords = keywords || defaultKeywords;

  // Default Organization structured data (always present)
  const organizationData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Qualitati',
    url: 'https://qualitati.com',
    logo: 'https://qualitati.com/logo512.png',
    description: 'AI-powered qualitative research platform for academics and researchers.',
    sameAs: []
  };

  // WebSite structured data for sitelinks search box
  const websiteData = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'Qualitati',
    url: 'https://qualitati.com',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://qualitati.com/research?q={search_term_string}',
      'query-input': 'required name=search_term_string'
    }
  };

  return (
    <Helmet>
      {/* HTML lang attribute */}
      <html lang={lang} />

      {/* Basic Meta Tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />

      {/* Canonical URL */}
      <link rel="canonical" href={canonicalUrl} />

      {/* Robots Meta */}
      {noindex ? (
        <meta name="robots" content="noindex, nofollow" />
      ) : (
        <meta name="robots" content="index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1" />
      )}

      {/* Open Graph Meta Tags */}
      <meta property="og:title" content={pageOgTitle} />
      <meta property="og:description" content={pageOgDescription} />
      <meta property="og:image" content={pageOgImage} />
      <meta property="og:image:width" content="512" />
      <meta property="og:image:height" content="512" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={productName} />
      <meta property="og:locale" content={lang === 'en' ? 'en_US' : lang} />

      {/* Twitter Card Meta Tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageOgTitle} />
      <meta name="twitter:description" content={pageOgDescription} />
      <meta name="twitter:image" content={pageOgImage} />

      {/* Additional Meta Tags */}
      <meta name="author" content="Qualitati" />
      <meta name="application-name" content="Qualitati" />
      <meta name="apple-mobile-web-app-title" content="Qualitati" />

      {/* hreflang for multilingual SEO */}
      {alternateLanguages && alternateLanguages.map(({ lang: altLang, url }) => (
        <link key={altLang} rel="alternate" hrefLang={altLang} href={url} />
      ))}

      {/* Organization Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(organizationData)}
      </script>

      {/* WebSite Structured Data (homepage only) */}
      {location.pathname === '/' && (
        <script type="application/ld+json">
          {JSON.stringify(websiteData)}
        </script>
      )}

      {/* Page-specific Structured Data — supports single object or array of objects */}
      {structuredData && (
        Array.isArray(structuredData)
          ? structuredData.map((item, i) => (
              <script key={i} type="application/ld+json">
                {JSON.stringify(item)}
              </script>
            ))
          : (
              <script type="application/ld+json">
                {JSON.stringify(structuredData)}
              </script>
            )
      )}
    </Helmet>
  );
};

export default SEO;
